import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FtoReportContext } from '../novelty-check-report-context';
import type { FtoReportState } from '../novelty-check-report-state';
import { Logger } from '@patsnap/tahiti-core';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { FtoTaskStep } from '@/domains/fto-report/enums';
import { FtoReportRetrievalResultReportModel } from '@/domains/fto-report/models/retrieval-result-report-model';
import { FtoReportTaskCreator } from '../../task-creator';
import { FtoReportTaskStatusModel } from '@/domains/fto-report/models/task-status-model';
import { NOVELTY_CHECK_TRIGGER_POINT_SUMMARY_MIN_LENGTH } from '@/domains/fto-report/constants';
import _ from 'lodash';
import wordsCount from 'words-count';

@injectable()
export class FtoReportInitialState implements FtoReportState {
    // eslint-disable-next-line max-params
    constructor(
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(FtoReportRetrievalResultReportModel)
        private readonly _retrievalResultReportModel: FtoReportRetrievalResultReportModel,
        @inject(FtoReportTaskCreator)
        private readonly _noveltyCheckTaskCreator: FtoReportTaskCreator,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(FtoReportTaskStatusModel)
        private readonly _noveltyCheckTaskStatusModel: FtoReportTaskStatusModel,
    ) { }

    getStep(): FtoTaskStep {
        return FtoTaskStep.INITIAL;
    }

    async enter(): Promise<void> {
        // 初始状态进入逻辑
        this._logger.info('进入初始状态');
    }

    async exit(): Promise<void> {
        // 离开初始状态的逻辑
        this._logger.info('离开初始状态');
    }

    async handleNext(context: FtoReportContext): Promise<void> {
        const text = this._retrievalResultReportModel.modelData.techEssential;
        const country = this._retrievalResultReportModel.modelData.country;
        const legalStatus = this._retrievalResultReportModel.modelData.legalStatus;
        const includeCompany = this._retrievalResultReportModel.modelData.includeCompany;
        const excludeCompany = this._retrievalResultReportModel.modelData.excludeCompany;
        const apdStart = this._retrievalResultReportModel.modelData.apdStart;
        const apdEnd = this._retrievalResultReportModel.modelData.apdEnd;
        await this._createTask({ text, country, legalStatus, includeCompany, excludeCompany, apdStart, apdEnd });
        const wordCount = wordsCount(text);
        if (wordCount > NOVELTY_CHECK_TRIGGER_POINT_SUMMARY_MIN_LENGTH) {
            const summaryState = context.getState(FtoTaskStep.SUMMARY_START);
            await context.transitionTo(summaryState);
            return;
        }
        // 从初始状态进入技术特征识别开始状态
        const techFeatureState = context.getState(FtoTaskStep.TECH_FEATURE_PULL_START);
        await context.transitionTo(techFeatureState);
    }

    async handlePrevious(): Promise<void> {
        // 初始状态没有上一步
        this._logger.info('初始状态没有上一步');
    }

    private get _isNewTask() {
        return this._noveltyCheckTaskStatusModel.modelData.taskStep === FtoTaskStep.INITIAL;
    }

    private async _createTask(payload: { text: string, country: string[], legalStatus: number[], includeCompany: string[], excludeCompany: string[], apdStart: Date | null, apdEnd: Date | null }): Promise<void> {
        const taskId = this._currentTaskService.taskId;
        if (this._isNewTask) {
            await this._noveltyCheckTaskCreator.createTask({
                text: payload.text,
                country: payload.country,
                taskId,
                legalStatus: payload.legalStatus,
                includeCompany: payload.includeCompany,
                excludeCompany: payload.excludeCompany,
                apdStart: payload.apdStart,
                apdEnd: payload.apdEnd,
            });
        }
    }
}
